/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import Badge from '@atlaskit/badge';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	type TimeTrackingOptions,
	timeTrackingFormatter,
} from '@atlassian/jira-time-tracking-formatter';
import { useFormatStoryPointsTotal } from '../../../../../../common/hooks/use-format-story-points-total';
import type { EstimationType } from '../../../../../../model/types';
import type { Assignee } from '../../../types';
import messages from './messages';
import { useSortAssignees } from './utils';

type Props = {
	isStoryPointsEnabled: boolean;
	isOriginalEstimationEnabled: boolean;
	isRemainingTimeEstimationEnabled: boolean;
	includeSubtasks: boolean;
	assignees: Assignee[];
	unassigned?: Assignee;
	timeTrackingOptions: TimeTrackingOptions;
	estimationType: EstimationType;
};

const SummaryTableBody = ({
	assignees,
	unassigned,
	isStoryPointsEnabled,
	isOriginalEstimationEnabled,
	isRemainingTimeEstimationEnabled,
	timeTrackingOptions,
	includeSubtasks,
	estimationType,
}: Props) => {
	const intl = useIntl();
	const { formatMessage } = intl;
	const formatStoryPointsTotal = useFormatStoryPointsTotal();
	const assigneesList = useSortAssignees(assignees, estimationType);

	return (
		<tbody css={styles}>
			{unassigned && (
				<tr>
					<td css={styles2}>
						<Avatar appearance="circle" size="small" />
						{formatMessage(messages.unassignedLabel)}
					</td>
					<td>{unassigned.issueCount}</td>
					{includeSubtasks && <td>{unassigned.subtaskCount}</td>}
					{isStoryPointsEnabled && (
						<td>
							<Badge max={1000}>{formatStoryPointsTotal(unassigned.storyPoints)}</Badge>
						</td>
					)}
					{isOriginalEstimationEnabled && (
						<td>
							<Badge>
								{timeTrackingFormatter(
									unassigned.originalTimeEstimateInSeconds,
									timeTrackingOptions,
									intl,
								)}
							</Badge>
						</td>
					)}
					{isRemainingTimeEstimationEnabled && (
						<td>
							<Badge>
								{timeTrackingFormatter(
									unassigned.remainingTimeEstimateInSeconds,
									timeTrackingOptions,
									intl,
								)}
							</Badge>
						</td>
					)}
				</tr>
			)}
			{assigneesList.map((assignee, i) => (
				<tr key={`table-row-key-${i}`}>
					<td css={styles2}>
						<Avatar
							src={assignee.profilePicture}
							appearance="circle"
							name={assignee.name}
							size="small"
						/>
						{assignee.name}
					</td>
					<td>{assignee.issueCount}</td>
					{includeSubtasks && <td>{assignee.subtaskCount}</td>}
					{isStoryPointsEnabled && (
						<td>
							<Badge max={1000}>{formatStoryPointsTotal(assignee.storyPoints)}</Badge>
						</td>
					)}
					{isOriginalEstimationEnabled && (
						<td>
							<Badge>
								{timeTrackingFormatter(
									assignee.originalTimeEstimateInSeconds,
									timeTrackingOptions,
									intl,
								)}
							</Badge>
						</td>
					)}
					{isRemainingTimeEstimationEnabled && (
						<td>
							<Badge>
								{timeTrackingFormatter(
									assignee.remainingTimeEstimateInSeconds,
									timeTrackingOptions,
									intl,
								)}
							</Badge>
						</td>
					)}
				</tr>
			))}
		</tbody>
	);
};

export default SummaryTableBody;
const styles = css({ borderTop: 'None' });
const styles2 = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100', '8px'),
});
